import { ETLCourse } from './types';

export const SSIS_AZURE_COURSE: ETLCourse = {
  id: 'ssis-azure-data-factory',
  title: 'Microsoft SSIS & Azure Data Factory Mastery',
  description: 'Complete guide to Microsoft ETL tools - from on-premises SSIS to cloud-native Azure Data Factory. Build enterprise-grade data integration solutions.',
  level: 'intermediate',
  totalDuration: '16-20 hours',
  image: '/api/placeholder/400/250',
  toolFocus: 'Microsoft Stack',
  marketDemand: 'high',
  salaryRange: '$90,000 - $150,000',
  prerequisites: [
    'SQL Server knowledge',
    'T-SQL proficiency',
    'Basic understanding of data warehousing',
    'Windows Server familiarity'
  ],
  learningOutcomes: [
    'Design and deploy SSIS packages',
    'Build Azure Data Factory pipelines',
    'Implement hybrid cloud ETL solutions',
    'Master data transformation patterns',
    'Set up monitoring and error handling',
    'Deploy solutions to production environments'
  ],
  modules: [
    {
      id: 'ssis-1',
      title: 'SSIS Fundamentals and Architecture',
      description: 'Master SQL Server Integration Services basics, architecture, and development environment',
      duration: '3 hours',
      difficulty: 'beginner',
      order: 1,
      objectives: [
        'Understand SSIS architecture and components',
        'Set up SSIS development environment',
        'Create basic data flow packages',
        'Implement control flow logic'
      ],
      content: `# SSIS Fundamentals and Architecture

## Introduction to SSIS

SQL Server Integration Services (SSIS) is Microsoft's platform for building enterprise-level data integration and data transformations solutions.

### Key Components

**1. Control Flow**
- Tasks that define workflow
- Containers for grouping tasks
- Precedence constraints for execution order

**2. Data Flow**
- Sources (where data comes from)
- Transformations (how data is modified)
- Destinations (where data goes)

**3. Event Handlers**
- Custom logic for package events
- Error handling and logging

**4. Package Explorer**
- Connection managers
- Log providers
- Variables and parameters

## Setting Up SSIS Development Environment

### Required Tools

\`\`\`
SQL Server Data Tools (SSDT) or
SQL Server Management Studio (SSMS) with SSIS extensions

Components needed:
- SQL Server Database Engine
- Integration Services
- SQL Server Data Tools
\`\`\`

### Creating Your First SSIS Project

1. **New Integration Services Project**
   - File → New → Project
   - Select Integration Services Project
   - Choose appropriate .NET Framework version

2. **Project Structure**
   \`\`\`
   SSIS Project/
   ├── Connection Managers/
   ├── SSIS Packages/
   ├── Project.params
   └── Package.dtsx
   \`\`\`

## Basic Package Development

### Control Flow Example

\`\`\`xml
<!-- Execute SQL Task -->
<ExecuteSQL Name="Create Staging Table">
  <SQLStatement>
    CREATE TABLE #TempCustomers (
      CustomerID INT,
      CustomerName NVARCHAR(100),
      Email NVARCHAR(255),
      LoadDate DATETIME DEFAULT GETDATE()
    )
  </SQLStatement>
</ExecuteSQL>

<!-- Data Flow Task -->
<DataFlow Name="Load Customer Data">
  <!-- Configured in Data Flow tab -->
</DataFlow>

<!-- Execute SQL Task -->
<ExecuteSQL Name="Validate Data">
  <SQLStatement>
    SELECT COUNT(*) FROM #TempCustomers
    WHERE CustomerID IS NULL OR CustomerName IS NULL
  </SQLStatement>
</ExecuteSQL>
\`\`\`

### Data Flow Components

**Source Example - OLE DB Source:**
\`\`\`sql
-- SQL Command for OLE DB Source
SELECT 
    CustomerID,
    FirstName + ' ' + LastName AS CustomerName,
    EmailAddress,
    Phone,
    GETDATE() as ExtractDate
FROM Sales.Customer c
INNER JOIN Person.Person p ON c.PersonID = p.BusinessEntityID
INNER JOIN Person.EmailAddress e ON p.BusinessEntityID = e.BusinessEntityID
WHERE c.ModifiedDate >= ?  -- Parameter for incremental load
\`\`\`

**Transformation Example - Derived Column:**
\`\`\`
Column Name: EmailDomain
Expression: RIGHT(EmailAddress, LEN(EmailAddress) - FINDSTRING(EmailAddress, "@", 1))

Column Name: CustomerKey
Expression: "CUST_" + (DT_STR, 10, 1252) CustomerID

Column Name: IsValidEmail
Expression: FINDSTRING(EmailAddress, "@", 1) > 0 && FINDSTRING(EmailAddress, ".", 1) > 0
\`\`\`

## Connection Managers

### OLE DB Connection Manager
\`\`\`
Server: localhost\\SQLEXPRESS
Database: AdventureWorks2019
Authentication: Windows Authentication

Connection String:
Data Source=localhost\\SQLEXPRESS;Initial Catalog=AdventureWorks2019;
Provider=SQLNCLI11.1;Integrated Security=SSPI;
\`\`\`

### Flat File Connection Manager
\`\`\`
Format: Delimited
Text Qualifier: "
Column Delimiter: ,
Header Row Delimiter: {CR}{LF}
Columns:
- CustomerID (DT_I4)
- CustomerName (DT_STR, 100)
- Email (DT_STR, 255)
\`\`\`

## Variables and Parameters

### Package Variables
\`\`\`
Name: LastExtractDate
Data Type: DateTime
Scope: Package
Value: 1/1/2020

Name: BatchSize
Data Type: Int32
Scope: Package
Value: 10000

Name: ErrorCount
Data Type: Int32
Scope: Package
Value: 0
\`\`\`

### Using Variables in SQL
\`\`\`sql
-- In Execute SQL Task
SELECT COUNT(*) as RecordCount
FROM Customer
WHERE ModifiedDate >= ?

-- Parameter Mapping:
-- Parameter Name: 0
-- Variable Name: User::LastExtractDate
-- Data Type: DATE
\`\`\`

## Error Handling Basics

### Control Flow Error Handling
\`\`\`
Precedence Constraint Configuration:
- Evaluation Operation: Expression and Constraint
- Value: Failure
- Expression: @[User::ErrorCount] < 10
\`\`\`

### Data Flow Error Handling
\`\`\`
OLE DB Destination Error Output:
- Error: Redirect Row
- Truncation: Redirect Row

Connect Error Output to:
- Flat File Destination (error log)
- Row Count Transformation (count errors)
\`\`\`

## Package Configuration Patterns

### Environment-Specific Settings
\`\`\`xml
<!-- Package Configuration -->
<Configuration>
  <Property Path="\\Package.Connections[DestDB].Properties[ConnectionString]">
    <Value>#{DatabaseConnectionString}#</Value>
  </Property>
  
  <Property Path="\\Package.Variables[User::BatchSize].Properties[Value]">
    <Value>#{ETLBatchSize}#</Value>
  </Property>
</Configuration>
\`\`\`

## Logging and Monitoring

### Built-in Logging
\`\`\`
Log Providers:
- Text File Log Provider
- SQL Server Log Provider
- Windows Event Log Provider

Events to Log:
- OnError
- OnWarning
- OnInformation
- OnPreExecute
- OnPostExecute
\`\`\`

### Custom Logging
\`\`\`sql
-- Custom log table
CREATE TABLE SSISLog (
    LogID INT IDENTITY(1,1) PRIMARY KEY,
    PackageName NVARCHAR(255),
    TaskName NVARCHAR(255),
    StartTime DATETIME,
    EndTime DATETIME,
    RowsProcessed INT,
    Status NVARCHAR(50),
    ErrorMessage NVARCHAR(MAX)
)

-- Insert in Script Task
Dts.Variables["User::RowsProcessed"].Value = 1000;
Dts.Variables["User::TaskStatus"].Value = "Success";
\`\`\`

## Performance Best Practices

### Data Flow Optimization
1. **Buffer Sizing**
   - DefaultBufferMaxRows: 10000
   - DefaultBufferSize: 10MB
   - Adjust based on available memory

2. **Parallelism**
   - Use multiple data flows
   - Partition large datasets
   - Leverage EngineThreads property

3. **Source Optimization**
   - Use SQL commands instead of table/view
   - Implement proper indexing
   - Use WHERE clauses to filter early

### Memory Management
\`\`\`xml
<!-- Package Properties -->
<Property Name="MaxConcurrentExecutables">4</Property>
<Property Name="EngineThreads">10</Property>
<Property Name="BufferTempStoragePath">D:\\Temp</Property>
<Property Name="BLOBTempStoragePath">D:\\Temp</Property>
\`\`\`

## Next Steps

In the next module, we'll cover:
- Advanced SSIS transformations
- Package deployment strategies
- Integration with SQL Server Agent
- Monitoring and maintenance
- Migration to Azure Data Factory`
    },
    {
      id: 'ssis-2',
      title: 'Advanced SSIS Transformations and Patterns',
      description: 'Master complex data transformation scenarios, slowly changing dimensions, and performance optimization',
      duration: '4 hours',
      difficulty: 'intermediate',
      order: 2,
      objectives: [
        'Implement slowly changing dimensions',
        'Build complex transformation patterns',
        'Optimize package performance',
        'Handle large datasets efficiently'
      ],
      content: `# Advanced SSIS Transformations and Patterns

## Slowly Changing Dimensions (SCD)

### SCD Type 1 - Overwrite
\`\`\`sql
-- Source Query for SCD Type 1
SELECT 
    CustomerID,
    CustomerName,
    Email,
    City,
    State,
    HASHBYTES('SHA2_256', 
        CONCAT(CustomerName, Email, City, State)) as RowHash
FROM SourceCustomers
\`\`\`

**SSIS Implementation:**
1. **Lookup Transformation**
   - Lookup existing records
   - Cache mode: Full cache
   - Connection: Target dimension table

2. **Conditional Split**
   - New Records: ISNULL(DimCustomerKey)
   - Changed Records: !ISNULL(DimCustomerKey) && SourceRowHash != TargetRowHash
   - Unchanged: !ISNULL(DimCustomerKey) && SourceRowHash == TargetRowHash

3. **Destinations**
   - Insert for new records
   - Update for changed records

### SCD Type 2 - Historical Tracking
\`\`\`sql
-- Dimension Table Structure
CREATE TABLE DimCustomer (
    CustomerKey INT IDENTITY(1,1) PRIMARY KEY,
    CustomerID INT NOT NULL,
    CustomerName NVARCHAR(100),
    Email NVARCHAR(255),
    City NVARCHAR(50),
    State NVARCHAR(50),
    EffectiveDate DATE NOT NULL,
    ExpirationDate DATE NULL,
    IsCurrent BIT NOT NULL DEFAULT 1,
    CreatedDate DATETIME DEFAULT GETDATE(),
    ModifiedDate DATETIME DEFAULT GETDATE()
)
\`\`\`

**SCD Type 2 SSIS Pattern:**

1. **Data Flow Components**
   \`\`\`
   Source → Lookup (Current Records) → Conditional Split
   ├── New Records → Insert
   ├── Changed Records → Update (Expire Old) + Insert (New)
   └── Unchanged Records → (No Action)
   \`\`\`

2. **Lookup Transformation Configuration**
   \`\`\`sql
   -- Lookup Query
   SELECT CustomerID, CustomerKey, CustomerName, Email, 
          City, State, IsCurrent
   FROM DimCustomer 
   WHERE IsCurrent = 1
   \`\`\`

3. **Conditional Split Expressions**
   \`\`\`
   New Record: ISNULL(CustomerKey)
   
   Changed Record: !ISNULL(CustomerKey) && 
                   (CustomerName != Lookup_CustomerName ||
                    Email != Lookup_Email ||
                    City != Lookup_City ||
                    State != Lookup_State)
   \`\`\`

4. **Script Component for Date Logic**
   \`\`\`csharp
   public override void Input0_ProcessInputRow(Input0Buffer Row)
   {
       Row.EffectiveDate = DateTime.Now.Date;
       Row.ExpirationDate_IsNull = true;  // NULL for current record
       Row.IsCurrent = true;
   }
   \`\`\`

## Advanced Transformations

### Fuzzy Lookup for Data Cleansing
\`\`\`
Configuration:
- Reference Table: CleanCustomerNames
- Similarity Threshold: 0.8
- Maximum Matches: 1

Output Columns:
- _Similarity (similarity score)
- _Confidence (confidence level)
- CleanName (corrected name)
\`\`\`

### Data Mining Query Transformation
\`\`\`sql
-- Predictive model query
SELECT t.CustomerID,
       PredictHistogram([Customer Segmentation].[Segment]) as PredictedSegment
FROM [Customer Segmentation] 
PREDICTION JOIN 
OPENQUERY(OLEDB, 'SELECT CustomerID, Age, Income, PurchaseHistory 
                  FROM CustomerFeatures') AS t
ON [Customer Segmentation].[Age] = t.[Age] AND
   [Customer Segmentation].[Income] = t.[Income]
\`\`\`

### Pivot and Unpivot Transformations

**Pivot Example:**
\`\`\`
Input Data:
CustomerID | Month | Sales
1          | Jan   | 1000
1          | Feb   | 1200
1          | Mar   | 1100

Pivot Configuration:
- Set Key: CustomerID
- Pivot Key: Month
- Pivot Value: Sales

Output:
CustomerID | Jan  | Feb  | Mar
1          | 1000 | 1200 | 1100
\`\`\`

**Unpivot Example:**
\`\`\`
Input Data:
CustomerID | Q1Sales | Q2Sales | Q3Sales | Q4Sales
1          | 3000    | 3500    | 3200    | 4000

Unpivot Configuration:
- Pass Through: CustomerID
- Pivot Key Column Name: Quarter
- Pivot Value Column Name: SalesAmount
- Columns to Unpivot: Q1Sales, Q2Sales, Q3Sales, Q4Sales

Output:
CustomerID | Quarter  | SalesAmount
1          | Q1Sales  | 3000
1          | Q2Sales  | 3500
\`\`\`

## Complex Data Integration Patterns

### Master Data Management (MDM)
\`\`\`sql
-- Golden Record Creation
WITH CustomerSources AS (
    SELECT CustomerID, CustomerName, Email, Phone, 'CRM' as Source,
           ROW_NUMBER() OVER (PARTITION BY Email ORDER BY ModifiedDate DESC) as rn
    FROM CRMCustomers
    
    UNION ALL
    
    SELECT CustomerID, CustomerName, Email, Phone, 'ERP' as Source,
           ROW_NUMBER() OVER (PARTITION BY Email ORDER BY ModifiedDate DESC) as rn
    FROM ERPCustomers
),
GoldenRecord AS (
    SELECT 
        FIRST_VALUE(CustomerName) OVER (PARTITION BY Email ORDER BY 
            CASE Source WHEN 'CRM' THEN 1 WHEN 'ERP' THEN 2 END) as BestName,
        FIRST_VALUE(Phone) OVER (PARTITION BY Email ORDER BY 
            LEN(Phone) DESC) as BestPhone,
        Email
    FROM CustomerSources
    WHERE rn = 1
)
\`\`\`

### Change Data Capture (CDC) Processing
\`\`\`sql
-- CDC Source Query
SELECT 
    CustomerID,
    CustomerName,
    Email,
    __$operation,
    __$start_lsn,
    __$seqval
FROM cdc.dbo_Customers_CT
WHERE __$start_lsn >= ? AND __$start_lsn <= ?
ORDER BY __$start_lsn, __$seqval
\`\`\`

**CDC Processing Logic:**
\`\`\`
Conditional Split on __$operation:
- Insert (2): Route to Insert destination
- Delete (1): Route to Delete destination  
- Update Before (3): Skip
- Update After (4): Route to Update destination
\`\`\`

## Performance Optimization Strategies

### Parallel Processing Pattern
\`\`\`xml
<!-- Multiple Data Flows for Parallel Processing -->
<DataFlow Name="Process_Customers_1_10000">
  <OLEDBSource>
    SELECT * FROM Customers WHERE CustomerID BETWEEN 1 AND 10000
  </OLEDBSource>
</DataFlow>

<DataFlow Name="Process_Customers_10001_20000">
  <OLEDBSource>
    SELECT * FROM Customers WHERE CustomerID BETWEEN 10001 AND 20000
  </OLEDBSource>
</DataFlow>
\`\`\`

### Bulk Insert Optimization
\`\`\`
OLE DB Destination Settings:
- Data Access Mode: Table or view - fast load
- Table Lock: True
- Check Constraints: False
- Keep Identity: False
- Keep Nulls: True
- Rows per Batch: 10000
- Maximum Insert Commit Size: 0 (unlimited)
\`\`\`

### Memory Management
\`\`\`csharp
// Script Component for Memory Management
private int bufferCount = 0;
private const int maxBufferSize = 10000;

public override void Input0_ProcessInputRow(Input0Buffer Row)
{
    bufferCount++;
    
    // Process row
    ProcessRow(Row);
    
    // Force buffer flush when limit reached
    if (bufferCount >= maxBufferSize)
    {
        Output0Buffer.SetEndOfRowset();
        bufferCount = 0;
    }
}
\`\`\`

## Error Handling and Data Quality

### Comprehensive Error Handling Framework
\`\`\`sql
-- Error Log Table
CREATE TABLE ETLErrorLog (
    ErrorLogID INT IDENTITY(1,1) PRIMARY KEY,
    PackageName NVARCHAR(255),
    TaskName NVARCHAR(255),
    SourceTable NVARCHAR(100),
    ErrorCode INT,
    ErrorDescription NVARCHAR(MAX),
    ErrorData NVARCHAR(MAX),
    CreatedDate DATETIME DEFAULT GETDATE()
)
\`\`\`

### Data Quality Rules Engine
\`\`\`csharp
// Script Component for Data Quality
public override void Input0_ProcessInputRow(Input0Buffer Row)
{
    var qualityScore = 0;
    var issues = new List<string>();
    
    // Email validation
    if (IsValidEmail(Row.Email))
        qualityScore += 25;
    else
        issues.Add("Invalid email format");
    
    // Phone validation
    if (IsValidPhone(Row.Phone))
        qualityScore += 25;
    else
        issues.Add("Invalid phone format");
    
    // Name validation
    if (!string.IsNullOrEmpty(Row.CustomerName) && Row.CustomerName.Length > 2)
        qualityScore += 25;
    else
        issues.Add("Invalid or missing name");
    
    // Address validation
    if (!string.IsNullOrEmpty(Row.Address))
        qualityScore += 25;
    else
        issues.Add("Missing address");
    
    Row.QualityScore = qualityScore;
    Row.QualityIssues = string.Join("; ", issues);
    
    // Route based on quality
    if (qualityScore >= 75)
        Row.QualityTier = "Gold";
    else if (qualityScore >= 50)
        Row.QualityTier = "Silver";
    else
        Row.QualityTier = "Bronze";
}
\`\`\`

## Advanced Package Patterns

### Dynamic Package Execution
\`\`\`csharp
// Execute Package Task - Dynamic
string packagePath = Dts.Variables["User::PackagePath"].Value.ToString();
string connectionString = Dts.Variables["User::ConnectionString"].Value.ToString();

Microsoft.SqlServer.Dts.Runtime.Application app = new Application();
Package pkg = app.LoadPackage(packagePath, null);

pkg.Connections["SourceDB"].ConnectionString = connectionString;

DTSExecResult result = pkg.Execute();

if (result != DTSExecResult.Success)
{
    Dts.TaskResult = (int)ScriptResults.Failure;
}
\`\`\`

### Configuration-Driven ETL
\`\`\`sql
-- ETL Configuration Table
CREATE TABLE ETLConfiguration (
    ConfigID INT IDENTITY(1,1) PRIMARY KEY,
    SourceTable NVARCHAR(100),
    TargetTable NVARCHAR(100),
    SelectStatement NVARCHAR(MAX),
    IsActive BIT DEFAULT 1,
    LastProcessed DATETIME,
    RowsProcessed INT
)

-- Dynamic source generation
DECLARE @sql NVARCHAR(MAX)
SELECT @sql = SelectStatement 
FROM ETLConfiguration 
WHERE SourceTable = ? AND IsActive = 1
\`\`\`

## Next Steps

In the next module, we'll transition to:
- Azure Data Factory fundamentals
- Hybrid cloud integration patterns
- Migration strategies from SSIS to ADF
- Modern cloud-native ETL approaches`
    },
    {
      id: 'ssis-3',
      title: 'Azure Data Factory and Cloud Migration',
      description: 'Transition from SSIS to Azure Data Factory, implement hybrid solutions, and master cloud-native ETL',
      duration: '4 hours',
      difficulty: 'advanced',
      order: 3,
      objectives: [
        'Migrate SSIS packages to Azure Data Factory',
        'Build hybrid cloud ETL solutions',
        'Implement CI/CD for data pipelines',
        'Monitor and optimize cloud data pipelines'
      ],
      content: `# Azure Data Factory and Cloud Migration

## Introduction to Azure Data Factory

Azure Data Factory (ADF) is Microsoft's cloud-native data integration service that enables you to create, schedule, and orchestrate data workflows at scale.

### Key Components

**1. Pipelines**
- Logical grouping of activities
- Define workflow and control flow
- Can be parameterized and reused

**2. Activities**
- Processing units within pipelines
- Copy data, transform data, control flow
- Can be chained and run in parallel

**3. Datasets**
- Named views of data
- Point to data in linked services
- Define data structure and location

**4. Linked Services**
- Connection strings to external resources
- Similar to connection managers in SSIS
- Support various data sources and destinations

**5. Integration Runtimes**
- Compute infrastructure for data integration
- Azure IR, Self-hosted IR, Azure-SSIS IR

## Setting Up Azure Data Factory

### Creating ADF Instance

\`\`\`json
{
  "type": "Microsoft.DataFactory/factories",
  "apiVersion": "2018-06-01",
  "name": "myDataFactory",
  "location": "East US",
  "properties": {
    "repoConfiguration": {
      "type": "FactoryGitHubConfiguration",
      "accountName": "myGitHubAccount",
      "repositoryName": "adf-repo",
      "collaborationBranch": "main",
      "rootFolder": "/"
    }
  }
}
\`\`\`

### Integration Runtime Setup

**Azure Integration Runtime:**
\`\`\`json
{
  "name": "AutoResolveIntegrationRuntime",
  "type": "Microsoft.DataFactory/factories/integrationruntimes",
  "properties": {
    "type": "Managed",
    "managedVirtualNetwork": {
      "type": "ManagedVirtualNetworkReference",
      "referenceName": "default"
    },
    "typeProperties": {
      "computeProperties": {
        "location": "AutoResolve",
        "dataFlowProperties": {
          "computeType": "General",
          "coreCount": 8,
          "timeToLive": 10
        }
      }
    }
  }
}
\`\`\`

**Self-hosted Integration Runtime:**
\`\`\`powershell
# Install Self-hosted IR
$gatewayKey = "YOUR_GATEWAY_KEY"
& "C:\\Program Files\\Microsoft Integration Runtime\\5.0\\Shared\\dmgcmd.exe" -rgkey $gatewayKey
\`\`\`

## Migrating SSIS to ADF

### SSIS Package Execution in ADF

**1. Lift and Shift Approach:**
\`\`\`json
{
  "name": "ExecuteSSISPackage",
  "type": "ExecuteSSISPackage",
  "typeProperties": {
    "packageLocation": {
      "packagePath": "/SSISDB/MyProject/MyPackage.dtsx"
    },
    "runtime": "x64",
    "loggingLevel": "Basic",
    "environmentPath": "/SSISDB/MyProject/MyEnvironment",
    "propertyOverrides": {
      "\\Package.Variables[User::SourcePath].Properties[Value]": "new_source_path"
    }
  },
  "linkedServiceName": {
    "referenceName": "AzureSSISLinkedService",
    "type": "LinkedServiceReference"
  }
}
\`\`\`

**2. Native ADF Conversion:**

*SSIS Data Flow → ADF Copy Activity:*
\`\`\`json
{
  "name": "CopyCustomerData",
  "type": "Copy",
  "inputs": [
    {
      "referenceName": "SourceDataset",
      "type": "DatasetReference"
    }
  ],
  "outputs": [
    {
      "referenceName": "SinkDataset", 
      "type": "DatasetReference"
    }
  ],
  "typeProperties": {
    "source": {
      "type": "SqlSource",
      "sqlReaderQuery": "SELECT CustomerID, CustomerName, Email FROM Customers WHERE ModifiedDate >= '@{pipeline().parameters.LastRunDate}'"
    },
    "sink": {
      "type": "SqlSink",
      "writeBehavior": "insert",
      "tableOption": "autoCreate"
    },
    "enableStaging": true,
    "stagingSettings": {
      "linkedServiceName": {
        "referenceName": "AzureBlobStorage",
        "type": "LinkedServiceReference"
      },
      "path": "staging/customer-data"
    }
  }
}
\`\`\`

### Mapping Data Flows (ADF's ETL Engine)

**Data Flow Definition:**
\`\`\`json
{
  "name": "CustomerDataTransformation",
  "type": "MappingDataFlow",
  "typeProperties": {
    "sources": [
      {
        "dataset": {
          "referenceName": "SourceCustomers",
          "type": "DatasetReference"
        },
        "name": "CustomersSource"
      }
    ],
    "sinks": [
      {
        "dataset": {
          "referenceName": "DimCustomers",
          "type": "DatasetReference"
        },
        "name": "CustomersDimension"
      }
    ],
    "transformations": [
      {
        "name": "DerivedColumns",
        "type": "DerivedColumn",
        "typeProperties": {
          "columns": [
            {
              "name": "FullName",
              "expression": "concat(FirstName, ' ', LastName)"
            },
            {
              "name": "EmailDomain",
              "expression": "split(Email, '@')[2]"
            },
            {
              "name": "LoadDate",
              "expression": "currentTimestamp()"
            }
          ]
        }
      },
      {
        "name": "FilterValidRecords",
        "type": "Filter",
        "typeProperties": {
          "condition": {
            "expression": "!isNull(Email) && contains(Email, '@')"
          }
        }
      }
    ]
  }
}
\`\`\`

## Advanced ADF Patterns

### Slowly Changing Dimensions in Data Flow

**SCD Type 2 Implementation:**
\`\`\`json
{
  "name": "SCD2CustomerDimension",
  "transformations": [
    {
      "name": "LookupExistingRecords",
      "type": "Lookup",
      "typeProperties": {
        "right": "ExistingCustomers",
        "leftColumns": ["CustomerID"],
        "rightColumns": ["CustomerID"],
        "multiple": false,
        "pickup": "first"
      }
    },
    {
      "name": "DetectChanges",
      "type": "ConditionalSplit",
      "typeProperties": {
        "conditions": [
          {
            "name": "NewRecords",
            "expression": "isNull(ExistingCustomerKey)"
          },
          {
            "name": "ChangedRecords", 
            "expression": "!isNull(ExistingCustomerKey) && (CustomerName != ExistingCustomerName || Email != ExistingEmail)"
          }
        ],
        "defaultName": "UnchangedRecords"
      }
    },
    {
      "name": "NewRecordTransform",
      "type": "DerivedColumn",
      "typeProperties": {
        "columns": [
          {
            "name": "EffectiveDate",
            "expression": "currentDate()"
          },
          {
            "name": "ExpirationDate",
            "expression": "toDate('9999-12-31')"
          },
          {
            "name": "IsCurrent",
            "expression": "true()"
          }
        ]
      }
    }
  ]
}
\`\`\`

### Dynamic Pipeline Execution

**Metadata-Driven Pipeline:**
\`\`\`json
{
  "name": "MetadataDrivenPipeline",
  "activities": [
    {
      "name": "GetTableList",
      "type": "Lookup",
      "typeProperties": {
        "source": {
          "type": "SqlSource",
          "sqlReaderQuery": "SELECT SourceTable, TargetTable, SelectQuery FROM ETLConfiguration WHERE IsActive = 1"
        },
        "firstRowOnly": false
      }
    },
    {
      "name": "ForEachTable",
      "type": "ForEach",
      "dependsOn": [
        {
          "activity": "GetTableList",
          "dependencyConditions": ["Succeeded"]
        }
      ],
      "typeProperties": {
        "items": {
          "value": "@activity('GetTableList').output.value",
          "type": "Expression"
        },
        "isSequential": false,
        "batchCount": 10,
        "activities": [
          {
            "name": "CopyTableData",
            "type": "Copy",
            "typeProperties": {
              "source": {
                "type": "SqlSource",
                "sqlReaderQuery": {
                  "value": "@item().SelectQuery",
                  "type": "Expression"
                }
              },
              "sink": {
                "type": "SqlSink",
                "tableOption": "autoCreate"
              }
            }
          }
        ]
      }
    }
  ]
}
\`\`\`

## Hybrid Cloud Integration

### On-Premises to Cloud Migration Pattern

**Staged Migration Approach:**

*Phase 1: Hybrid Setup*
\`\`\`json
{
  "name": "HybridDataSync",
  "activities": [
    {
      "name": "ExtractFromOnPrem", 
      "type": "Copy",
      "typeProperties": {
        "source": {
          "type": "SqlSource",
          "sqlReaderQuery": "SELECT * FROM OnPremTable WHERE ModifiedDate >= '@{pipeline().parameters.LastSyncDate}'"
        },
        "sink": {
          "type": "AzureBlobSink",
          "blobWriterAddHeader": true
        }
      },
      "linkedServiceName": {
        "referenceName": "OnPremSqlServer",
        "type": "LinkedServiceReference"
      }
    },
    {
      "name": "ProcessInCloud",
      "type": "ExecuteDataFlow", 
      "dependsOn": [
        {
          "activity": "ExtractFromOnPrem",
          "dependencyConditions": ["Succeeded"]
        }
      ],
      "typeProperties": {
        "dataFlow": {
          "referenceName": "CloudTransformationDataFlow",
          "type": "DataFlowReference"
        }
      }
    }
  ]
}
\`\`\`

*Phase 2: Full Cloud Migration*
\`\`\`json
{
  "name": "CloudNativeETL",
  "activities": [
    {
      "name": "IngestFromSources",
      "type": "Copy",
      "typeProperties": {
        "source": {
          "type": "RestSource",
          "httpRequestTimeout": "00:01:40",
          "requestInterval": "00:00:01"
        }
      }
    },
    {
      "name": "TransformData",
      "type": "ExecuteDataFlow",
      "typeProperties": {
        "dataFlow": {
          "referenceName": "ComprehensiveTransformation",
          "type": "DataFlowReference"
        },
        "compute": {
          "coreCount": 8,
          "computeType": "General"
        }
      }
    }
  ]
}
\`\`\`

## CI/CD for Data Pipelines

### Azure DevOps Integration

**Build Pipeline (azure-pipelines.yml):**
\`\`\`yaml
trigger:
- main

pool:
  vmImage: 'ubuntu-latest'

stages:
- stage: Validate
  jobs:
  - job: ValidateADF
    steps:
    - task: NodeTool@0
      inputs:
        versionSpec: '14.x'
    
    - task: Npm@1
      inputs:
        command: 'install'
        workingDir: '$(Build.Repository.LocalPath)/adf'
    
    - task: Npm@1
      displayName: 'Validate ADF'
      inputs:
        command: 'custom'
        customCommand: 'run build validate $(Build.Repository.LocalPath)/adf /subscriptions/$(SUBSCRIPTION_ID)/resourceGroups/$(RESOURCE_GROUP)/providers/Microsoft.DataFactory/factories/$(DATA_FACTORY_NAME)'
        workingDir: '$(Build.Repository.LocalPath)/adf'

- stage: Deploy
  dependsOn: Validate
  condition: succeeded()
  jobs:
  - deployment: DeployADF
    environment: 'production'
    strategy:
      runOnce:
        deploy:
          steps:
          - task: AzureResourceManagerTemplateDeployment@3
            inputs:
              deploymentScope: 'Resource Group'
              azureResourceManagerConnection: '$(SERVICE_CONNECTION)'
              subscriptionId: '$(SUBSCRIPTION_ID)'
              action: 'Create Or Update Resource Group'
              resourceGroupName: '$(RESOURCE_GROUP)'
              location: 'East US'
              templateLocation: 'Linked artifact'
              csmFile: '$(Pipeline.Workspace)/adf-templates/ARMTemplateForFactory.json'
              csmParametersFile: '$(Pipeline.Workspace)/adf-templates/ARMTemplateParametersForFactory.json'
\`\`\`

### Environment Management

**Development Environment:**
\`\`\`json
{
  "dataFactoryName": "adf-dev",
  "linkedServices": {
    "AzureSqlDatabase": {
      "connectionString": "@{linkedService().connectionString}",
      "parameters": {
        "connectionString": "Server=dev-sql-server.database.windows.net;Database=DevDB;"
      }
    }
  }
}
\`\`\`

**Production Environment:**
\`\`\`json
{
  "dataFactoryName": "adf-prod", 
  "linkedServices": {
    "AzureSqlDatabase": {
      "connectionString": "@{linkedService().connectionString}",
      "parameters": {
        "connectionString": "Server=prod-sql-server.database.windows.net;Database=ProdDB;"
      }
    }
  }
}
\`\`\`

## Monitoring and Optimization

### Azure Monitor Integration

**Custom Metrics:**
\`\`\`json
{
  "name": "PipelineSuccessRate",
  "type": "Microsoft.Insights/metricAlerts",
  "properties": {
    "description": "Alert when pipeline success rate drops below 95%",
    "severity": 2,
    "enabled": true,
    "scopes": [
      "/subscriptions/{subscription-id}/resourceGroups/{resource-group}/providers/Microsoft.DataFactory/factories/{factory-name}"
    ],
    "criteria": {
      "odata.type": "Microsoft.Azure.Monitor.SingleResourceMultipleMetricCriteria",
      "allOf": [
        {
          "name": "PipelineSuccessRate",
          "metricName": "PipelineSucceededRuns",
          "operator": "LessThan",
          "threshold": 0.95,
          "timeAggregation": "Average"
        }
      ]
    }
  }
}
\`\`\`

### Performance Optimization

**Data Flow Optimization:**
\`\`\`json
{
  "compute": {
    "coreCount": 8,
    "computeType": "ComputeOptimized"
  },
  "staging": {
    "linkedService": {
      "referenceName": "AzureBlobStorage",
      "type": "LinkedServiceReference"
    },
    "folderPath": "staging/dataflow-temp"
  },
  "loggingLevel": "None",
  "runConcurrently": true
}
\`\`\`

**Copy Activity Optimization:**
\`\`\`json
{
  "enableStaging": true,
  "stagingSettings": {
    "linkedServiceName": {
      "referenceName": "AzureBlobStorage", 
      "type": "LinkedServiceReference"
    },
    "path": "staging",
    "enableCompression": true
  },
  "parallelCopies": 32,
  "dataIntegrationUnits": 256,
  "enableSkipIncompatibleRow": true,
  "logSettings": {
    "enableCopyActivityLog": true,
    "copyActivityLogSettings": {
      "logLevel": "Warning",
      "enableReliableLogging": false
    }
  }
}
\`\`\`

## Cost Optimization Strategies

### Resource Management
\`\`\`json
{
  "autoTerminate": {
    "idleTimeoutInMinutes": 10
  },
  "nodeTypes": {
    "compute": "Standard_D4s_v3",
    "driver": "Standard_D4s_v3"
  },
  "scaling": {
    "minNodeCount": 1,
    "maxNodeCount": 10,
    "targetUtilization": 0.8
  }
}
\`\`\`

### Pipeline Scheduling Optimization
\`\`\`json
{
  "trigger": {
    "type": "ScheduleTrigger",
    "typeProperties": {
      "recurrence": {
        "frequency": "Day",
        "interval": 1,
        "startTime": "2023-01-01T02:00:00.000Z",
        "timeZone": "UTC",
        "schedule": {
          "hours": [2],
          "minutes": [0]
        }
      }
    }
  }
}
\`\`\`

## Next Steps

You now have comprehensive knowledge of Microsoft's ETL stack! Consider exploring:
- Power BI integration with ADF
- Synapse Analytics for big data scenarios
- Real-time streaming with Event Hubs
- Advanced data governance with Purview
- Machine learning integration with Azure ML`
    }
  ]
};